import type { PrepackageBoundingBox, PrepackageDestination } from '../../types';

export type GeoHierarchyBounds = {
    swLat: number;
    swLng: number;
    neLat: number;
    neLng: number;
};

export type GeoHierarchyNode = {
    name: string;
    displayName: string;
    country?: string;
    bounds: GeoHierarchyBounds;
    hotelCount?: number;
    isCountry?: boolean;
    children?: GeoHierarchyNode[];
};

export type SearchIndexItem = {
    node: GeoHierarchyNode;
    type: 'continent' | 'country' | 'region' | 'destination';
    path: string[];
    country?: string;
};

export const MAX_PREPACKAGE_DESTINATIONS = 10;

export const sortedByHotelCount = <T extends { hotelCount?: number; name: string }>(nodes: T[]): T[] =>
    [...nodes].sort((a, b) => {
        const countDiff = (b.hotelCount ?? 0) - (a.hotelCount ?? 0);
        return countDiff === 0 ? a.name.localeCompare(b.name, 'nb') : countDiff;
    });

export const sortedByName = <T extends { name: string }>(nodes: T[]): T[] => [...nodes].sort((a, b) => a.name.localeCompare(b.name, 'nb'));

export const toBoundingBox = (bounds: GeoHierarchyBounds): PrepackageBoundingBox => ({
    minLat: bounds.swLat,
    minLon: bounds.swLng,
    maxLat: bounds.neLat,
    maxLon: bounds.neLng,
});

export const serializeBoundingBox = (boundingBox: PrepackageBoundingBox): string =>
    `${boundingBox.minLat},${boundingBox.minLon},${boundingBox.maxLat},${boundingBox.maxLon}`;

export const serializeDestination = (destination: PrepackageDestination): string =>
    [destination.label, destination.country ?? '', serializeBoundingBox(destination.boundingBox)].join('|');

export const nodeToDestination = (node: GeoHierarchyNode, country?: string): PrepackageDestination => ({
    label: node.displayName || node.name,
    country: node.country ?? country,
    boundingBox: toBoundingBox(node.bounds),
});

export const getDestinationKey = (destination: PrepackageDestination): string =>
    `${destination.label}|${serializeBoundingBox(destination.boundingBox)}`;

export const isSameDestination = (a: PrepackageDestination, b: PrepackageDestination): boolean =>
    getDestinationKey(a) === getDestinationKey(b);

type HierarchyEntry = SearchIndexItem & {
    ancestors: GeoHierarchyNode[];
    countryNode?: GeoHierarchyNode;
};

const hierarchyTypes: SearchIndexItem['type'][] = ['continent', 'country', 'region', 'destination'];

const getDisplayName = (node: GeoHierarchyNode): string => node.displayName || node.name;

const getHierarchyType = (depth: number): SearchIndexItem['type'] => hierarchyTypes[Math.min(depth, hierarchyTypes.length - 1)];

const hierarchyEntryCache = new WeakMap<GeoHierarchyNode[], HierarchyEntry[]>();

const toHierarchyEntries = (
    nodes: GeoHierarchyNode[],
    {
        depth = 0,
        path = [],
        ancestors = [],
        country,
        countryNode,
    }: {
        depth?: number;
        path?: string[];
        ancestors?: GeoHierarchyNode[];
        country?: string;
        countryNode?: GeoHierarchyNode;
    } = {},
): HierarchyEntry[] =>
    nodes.flatMap((node) => {
        const type = getHierarchyType(depth);
        const nodeCountry = type === 'country' ? getDisplayName(node) : country;
        const nodeCountryNode = type === 'country' ? node : countryNode;
        const entry: HierarchyEntry = {
            node,
            type,
            path,
            country: nodeCountry,
            ancestors,
            countryNode: nodeCountryNode,
        };

        return [
            entry,
            ...toHierarchyEntries(node.children ?? [], {
                depth: depth + 1,
                path: [...path, getDisplayName(node)],
                ancestors: [...ancestors, node],
                country: nodeCountry,
                countryNode: nodeCountryNode,
            }),
        ];
    });

const getHierarchyEntries = (hierarchy: GeoHierarchyNode[]): HierarchyEntry[] => {
    const cachedEntries = hierarchyEntryCache.get(hierarchy);
    if (cachedEntries) return cachedEntries;

    const entries = toHierarchyEntries(hierarchy);
    hierarchyEntryCache.set(hierarchy, entries);
    return entries;
};

export const buildSearchIndex = (hierarchy: GeoHierarchyNode[]): SearchIndexItem[] =>
    getHierarchyEntries(hierarchy).map(({ node, type, path, country }) => ({ node, type, path, country }));

const findEntryForDestination = (hierarchy: GeoHierarchyNode[], destination: PrepackageDestination): HierarchyEntry | undefined =>
    getHierarchyEntries(hierarchy).find((entry) => isSameDestination(nodeToDestination(entry.node, entry.country), destination));

export const getNodeAndDescendantDestinations = (node: GeoHierarchyNode, country?: string): PrepackageDestination[] => {
    const nodeCountry = node.isCountry ? getDisplayName(node) : country;

    return [
        nodeToDestination(node, nodeCountry),
        ...(node.children ?? []).flatMap((child) => getNodeAndDescendantDestinations(child, nodeCountry)),
    ];
};

export const findPathForDestination = (
    hierarchy: GeoHierarchyNode[],
    destination: PrepackageDestination,
): GeoHierarchyNode[] | undefined => {
    const entry = findEntryForDestination(hierarchy, destination);
    return entry ? [...entry.ancestors, entry.node] : undefined;
};

export const nodeHasSelectedDescendant = ({
    node,
    country,
    selected,
}: {
    node: GeoHierarchyNode;
    country?: string;
    selected: PrepackageDestination[];
}): boolean =>
    (node.children ?? [])
        .flatMap((child) => getNodeAndDescendantDestinations(child, country))
        .some((descendant) => selected.some((destination) => isSameDestination(destination, descendant)));

const getCountryNameFromPath = (path?: GeoHierarchyNode[]): string | undefined => {
    const countryNode = path?.[1];
    return countryNode ? getDisplayName(countryNode) : undefined;
};

const getAncestorDestinations = (path: GeoHierarchyNode[] | undefined, country?: string): PrepackageDestination[] =>
    (path ?? []).slice(0, -1).map((node, index) => nodeToDestination(node, index === 1 ? getDisplayName(node) : country));

const getDescendantDestinationsForSelection = (node: GeoHierarchyNode | undefined, country?: string): PrepackageDestination[] =>
    node ? getNodeAndDescendantDestinations(node, country).slice(1) : [];

const removeMatchingDestinations = (
    selected: PrepackageDestination[],
    destinationsToRemove: PrepackageDestination[],
): PrepackageDestination[] =>
    selected.filter((item) => !destinationsToRemove.some((destinationToRemove) => isSameDestination(destinationToRemove, item)));

export const toggleDestinationSelection = ({
    hierarchy,
    selected,
    destination,
}: {
    hierarchy: GeoHierarchyNode[];
    selected: PrepackageDestination[];
    destination: PrepackageDestination;
}): PrepackageDestination[] => {
    const isSelected = selected.some((item) => isSameDestination(item, destination));
    if (isSelected) return selected.filter((item) => !isSameDestination(item, destination));

    const path = findPathForDestination(hierarchy, destination);
    const selectedNode = path?.[path.length - 1];
    const countryName = getCountryNameFromPath(path);
    const conflictingDestinations = [
        ...getAncestorDestinations(path, countryName),
        ...getDescendantDestinationsForSelection(selectedNode, countryName),
    ];
    const nextSelected = removeMatchingDestinations(selected, conflictingDestinations);

    if (nextSelected.length >= MAX_PREPACKAGE_DESTINATIONS) return selected;

    return [...nextSelected, destination];
};

export const findPartialMatches = (query: string, index: SearchIndexItem[]): SearchIndexItem[] => {
    const normalized = query.toLowerCase().trim();

    for (let length = normalized.length - 1; length >= 2; length -= 1) {
        const partial = normalized.substring(0, length);
        const matches = index
            .filter((item) => item.node.name.toLowerCase().startsWith(partial))
            .filter((item) => item.type === 'country' || item.type === 'region')
            .sort((a, b) => (b.node.hotelCount ?? 0) - (a.node.hotelCount ?? 0))
            .slice(0, 4);

        if (matches.length > 0) return matches;
    }

    return [];
};

export const searchDestinations = (query: string, index: SearchIndexItem[]): SearchIndexItem[] => {
    const normalized = query.toLowerCase().trim();
    if (normalized.length < 2) return [];

    return index
        .filter((item) => item.node.name.toLowerCase().includes(normalized) || item.node.displayName.toLowerCase().includes(normalized))
        .sort((a, b) => {
            const aStarts = a.node.name.toLowerCase().startsWith(normalized) ? 0 : 1;
            const bStarts = b.node.name.toLowerCase().startsWith(normalized) ? 0 : 1;
            if (aStarts !== bStarts) return aStarts - bStarts;
            return a.node.name.localeCompare(b.node.name, 'nb');
        })
        .slice(0, 50);
};

export const getPopularCountries = (index: SearchIndexItem[], count: number): SearchIndexItem[] =>
    index
        .filter((item) => item.type === 'country')
        .sort((a, b) => (b.node.hotelCount ?? 0) - (a.node.hotelCount ?? 0))
        .slice(0, count);
